import { PassCodeVerifyRef } from 'Turn/TurnModals/PassCodeVerify';
import { AppointmentStatus } from 'Turn/services/constants';
import { Dropdown } from 'antd';
import ButtonConfirm from 'components/ButtonConfirm';
import { useState } from 'react';
import aptActions from 'services/actions/apt';
import shopSelectors from 'services/selectors/shop';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import {
  REVERT_LABEL,
  StatusStyle,
  UNDO_CHECK_IN_CONFIRM,
  getAllowedTransitions,
  getStatusLabel,
  getStatusStyle,
} from 'AppointmentList/status-transitions';

type Props = {
  appointmentId: number;
  status?: AppointmentStatus | number | null;
  verifyRef: React.RefObject<PassCodeVerifyRef>;
  /** Khoa trong luc bang dang tai, de cu bam thu hai khong ban them mot request nua. */
  disabled?: boolean;
};

/**
 * O status trong bang.
 *
 * Hai hinh dang, phan biet duoc tu xa ma khong can doc chu:
 * - to dac + mui ten      = bam duoc, mo dropdown de lui status
 * - chip phang, vien mong = chi de xem
 *
 * Buoi dang o NEW roi vao nhom thu hai: no da la trang thai dau, khong con gi de lui.
 */
const StatusCell = ({ appointmentId, status, verifyRef, disabled }: Props) => {
  const dispatch = useAppDispatch();
  const allowUndoCheckIn = shopSelectors.setting().allowUndoCheckIn;
  // Giu PIN da nhap cho toi luc bam dong y: thu tu bat buoc la PIN -> xac nhan -> goi API.
  const [pending, setPending] = useState<{ next: AppointmentStatus, pinCode: string } | null>(null);

  const options = getAllowedTransitions(status, { allowUndoCheckIn });
  const label = getStatusLabel(status);
  const style = getStatusStyle(status);
  const undoingCheckIn = status === AppointmentStatus.CHECK_IN;

  if (!options.length) {
    return <ReadOnlyChip $style={style}>{label}</ReadOnlyChip>;
  }

  const handleSelect = (next: AppointmentStatus) => {
    if (disabled) return;
    // forceVerifyAdmin luon hoi PIN, khac checkAdmin (bo qua o shop tat co
    // verifyPinChangeAppointment) — lui status la thao tac sua sai nen phai co nguoi chiu
    // trach nhiem.
    verifyRef.current?.forceVerifyAdmin(
      (pinCode) => setPending({ next, pinCode: pinCode || '' }),
      { title: 'PIN CODE', placeholder: 'Enter your Pin Code' },
    );
  };

  const handleConfirm = () => {
    if (!pending) return;
    dispatch(aptActions.changeAptStatus({
      appointmentId: String(appointmentId),
      status: pending.next,
      pinCode: pending.pinCode,
    }));
    setPending(null);
  };

  return (
    <>
      <Dropdown
        disabled={disabled}
        trigger={['click']}
        menu={{
          // Moi buoc deu dua ve NEW nen ghi hanh dong chu khong ghi ten status dich:
          // "Revert" noi dung viec no lam, con "New" thi nguoi doc phai tu suy ra.
          items: options.map(o => ({ key: String(o), label: REVERT_LABEL })),
          onClick: ({ key }) => handleSelect(Number(key) as AppointmentStatus),
        }}
      >
        <ActionButton type="button" $style={style} disabled={disabled}>
          {label}
          <Chevron viewBox="0 0 24 24" aria-hidden>
            <path d="M3.5 9.4 4.9 8l7.1 7.1L19.1 8l1.4 1.4-8.5 8.5z" />
          </Chevron>
        </ActionButton>
      </Dropdown>
      {/*
        Go check-in xoa ticket trong POS chu khong chi doi status, nen cau xac nhan phai noi
        ro dieu do. Dung chung mot cau voi lui status thuong la giau mat hau qua.
      */}
      <ButtonConfirm
        open={!!pending}
        onClose={() => setPending(null)}
        onYes={handleConfirm}
        modalTitle={undoingCheckIn ? UNDO_CHECK_IN_CONFIRM.title : 'REVERT STATUS'}
        okLabel={undoingCheckIn ? UNDO_CHECK_IN_CONFIRM.okLabel : 'REVERT'}
        message={undoingCheckIn
          ? UNDO_CHECK_IN_CONFIRM.message
          : `Set this appointment back to ${pending ? getStatusLabel(pending.next) : ''}?`}
      />
    </>
  );
};

export default StatusCell;

/** Cao bang nhau o ca hai hinh dang, de cac dong trong cot khong so le. */
const BOX = `
  height: 30px;
  min-width: 104px;
  padding: 0 12px;
  border-radius: 5px;
  font-family: Poppins;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
`;

const ReadOnlyChip = styled.span<{ $style: StatusStyle }>`
  ${BOX}
  background: ${({ $style }) => $style.soft};
  color: ${({ $style }) => $style.softText};
  border: 1px solid ${({ $style }) => $style.border};
  cursor: default;
  user-select: none;
`;

const ActionButton = styled.button<{ $style: StatusStyle }>`
  ${BOX}
  background: ${({ $style }) => $style.solid};
  color: #FFFFFF;
  border: none;
  cursor: pointer;
  user-select: none;
  transition: filter 0.15s ease, transform 0.05s ease;

  &:hover:not(:disabled) {
    filter: brightness(0.93);
  }

  /* Nhan xuong mot chut khi bam: phan hoi cho biet cu cham da an. */
  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  /* Khong lam mo khi khoa — bang da co lop loading rieng, mo them chi lam kho doc. */
  &:disabled {
    cursor: default;
  }
`;

const Chevron = styled.svg`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  fill: currentColor;
`;
